/** Llamadas HTTP del feature Socios (CRUD del padrón + cuenta corriente). */
import { apiDownload, apiRequest } from "../../lib/api";

export type EstadoSocio = "Activo" | "Inactivo" | "Fallecido";

export interface SocioRead {
  id: number;
  empresa_id: number;
  nombre: string;
  numero_socio: number | null;
  numero_libro: string | null;
  rut: string;
  telefono: string | null;
  correo: string | null;
  direccion: string | null;
  estado: EstadoSocio;
  es_socio: boolean;
  fecha_nacimiento: string | null;
  genero: string | null;
  estado_civil: string | null;
  fecha_ingreso: string | null;
  total_atenciones: number;
  fecha_primera_atencion: string | null;
  fecha_ultima_atencion: string | null;
  created_at: string;
  updated_at: string;
}

export interface SocioCreatePayload {
  nombre: string;
  rut: string;
  numero_socio?: number | null;
  numero_libro?: string | null;
  telefono?: string | null;
  correo?: string | null;
  direccion?: string | null;
  estado?: EstadoSocio;
  es_socio?: boolean;
  fecha_nacimiento?: string | null;
  genero?: string | null;
  estado_civil?: string | null;
  fecha_ingreso?: string | null;
}

export interface SocioUpdatePayload {
  nombre?: string;
  numero_socio?: number | null;
  numero_libro?: string | null;
  telefono?: string | null;
  correo?: string | null;
  direccion?: string | null;
  estado?: EstadoSocio;
  es_socio?: boolean;
  fecha_nacimiento?: string | null;
  genero?: string | null;
  estado_civil?: string | null;
  fecha_ingreso?: string | null;
}

export interface ListarSociosFilters {
  only_active?: boolean;
  only_socios?: boolean;
  search?: string | null;
}

export function listarSocios(
  f: ListarSociosFilters = {},
): Promise<SocioRead[]> {
  const qs = new URLSearchParams();
  if (f.only_active !== undefined) qs.set("only_active", String(f.only_active));
  if (f.only_socios) qs.set("only_socios", "true");
  if (f.search && f.search.trim()) qs.set("search", f.search.trim());
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  return apiRequest<SocioRead[]>(`/api/v1/socios${suffix}`);
}

export type CondicionFiltro = "todos" | "socios" | "clientes";
export type EstadoFiltro = "activos" | "Inactivo" | "Fallecido" | "todos";

export interface SociosPagina {
  items: SocioRead[];
  total: number;
  limit: number;
  offset: number;
}

/** Una página del padrón con el total (pantalla Socios, auditoría UX-05). */
export function paginaSocios(f: {
  condicion: CondicionFiltro;
  estado: EstadoFiltro;
  search: string | null;
  limit: number;
  offset: number;
}): Promise<SociosPagina> {
  const qs = new URLSearchParams({
    condicion: f.condicion,
    estado: f.estado,
    limit: String(f.limit),
    offset: String(f.offset),
  });
  if (f.search && f.search.trim()) qs.set("search", f.search.trim());
  return apiRequest<SociosPagina>(`/api/v1/socios/pagina?${qs.toString()}`);
}

export function obtenerSocio(id: number): Promise<SocioRead> {
  return apiRequest<SocioRead>(`/api/v1/socios/${id}`);
}

export function crearSocio(p: SocioCreatePayload): Promise<SocioRead> {
  return apiRequest<SocioRead>("/api/v1/socios", { method: "POST", body: p });
}

export function actualizarSocio(
  id: number,
  p: SocioUpdatePayload,
): Promise<SocioRead> {
  return apiRequest<SocioRead>(`/api/v1/socios/${id}`, {
    method: "PATCH",
    body: p,
  });
}

export function cambiarEstadoSocio(
  id: number,
  estado: EstadoSocio,
): Promise<SocioRead> {
  return apiRequest<SocioRead>(`/api/v1/socios/${id}/estado`, {
    method: "POST",
    body: { estado },
  });
}

// ────────────────────────────────────────────────────────────────────────
// Portal del socio: gestión de credencial (admin-side)
// ────────────────────────────────────────────────────────────────────────

export interface CredencialEstado {
  activo: boolean;
  last_login_at: string | null;
  creada_at: string;
}

export interface CredencialActivada {
  socio_id: number;
  password: string;
  activo: boolean;
  creada_at: string;
}

export function obtenerCredencialSocio(
  socioId: number,
): Promise<CredencialEstado | null> {
  return apiRequest<CredencialEstado | null>(
    `/api/v1/socios/${socioId}/credencial`,
  );
}

export function activarCredencialSocio(
  socioId: number,
): Promise<CredencialActivada> {
  return apiRequest<CredencialActivada>(
    `/api/v1/socios/${socioId}/credencial/activar`,
    { method: "POST" },
  );
}

export function revocarCredencialSocio(socioId: number): Promise<void> {
  return apiRequest<void>(`/api/v1/socios/${socioId}/credencial/revocar`, {
    method: "POST",
  });
}

export interface ContratoOperativo {
  id: number;
  socio_id: number;
  socio_nombre: string;
  socio_rut: string;
  arranque_id: number;
  arranque_codigo: string | null;
  arranque_direccion: string;
  sector_nombre: string | null;
  estado: string;
}

export interface LecturaRead {
  id: number;
  empresa_id: number;
  contrato_id: number;
  mes: number;
  anio: number;
  fecha_lectura: string;
  fecha_vencimiento: string | null;
  lectura_anterior: number;
  lectura_actual: number;
  consumo_m3: number;
  monto_cargo_fijo: number;
  monto_consumo: number;
  monto_alcantarillado: number;
  monto_subsidio: number;
  monto_multas: number;
  monto_total: number;
  /** Saldo pendiente real (monto_total - aplicado_no_anulado). Solo presente
   * en el endpoint /pagos/socios/{id}/pendientes. */
  saldo_actual?: number;
  estado: "Pendiente" | "Emitida" | "Pagada" | "Vencida" | "Anulada";
  numero_boleta: string | null;
  codigo_barras: string | null;
}

export type FormaPago =
  | "Efectivo"
  | "Transferencia"
  | "Tarjeta"
  | "Cheque"
  | "Saldo a Favor";
/** Lo que la caja registra: dinero que entró. `Saldo a Favor` lo aplica el sistema. */
export type FormaPagoCaja = Exclude<FormaPago, "Saldo a Favor">;

export interface DetallePagoRead {
  id: number;
  pago_id: number;
  lectura_id: number;
  monto_asignado: number;
}

export interface PagoRead {
  id: number;
  empresa_id: number;
  socio_id: number;
  fecha_pago: string;
  monto_pagado: number;
  forma_pago: FormaPago;
  nro_comprobante: string | null;
  observaciones: string | null;
  recibido_por_id: number | null;
  estado: "Activo" | "Anulado";
  motivo_anulacion: string | null;
  fecha_anulacion: string | null;
  anulado_por_id: number | null;
  detalles: DetallePagoRead[];
  created_at: string;
  updated_at: string;
}

export interface PagoTomarPayload {
  socio_id: number;
  monto_pagado: number;
  forma_pago?: FormaPagoCaja;
  fecha_pago?: string | null;
  nro_comprobante?: string | null;
  observaciones?: string | null;
  detalles?: { lectura_id: number; monto_asignado: number }[];
}

export interface MultaRead {
  id: number;
  empresa_id: number;
  socio_id: number;
  origen: string;
  reunion_id: number | null;
  monto: number;
  motivo: string;
  observacion: string | null;
  fecha_generacion: string;
  fecha_vencimiento: string | null;
  estado: "Pendiente" | "Facturada" | "Anulada";
  lectura_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface SubsidioRead {
  id: number;
  empresa_id: number;
  contrato_id: number;
  numero_decreto: string;
  fecha_decreto: string;
  fecha_inicio: string;
  fecha_vencimiento: string | null;
  porcentaje_subsidio: number;
  tope_m3: number;
  tope_monetario: number | null;
  activo: boolean;
}

export function fetchContratos(): Promise<ContratoOperativo[]> {
  return apiRequest<ContratoOperativo[]>("/api/v1/contratos");
}

export function fetchLecturasPendientesSocio(socioId: number): Promise<LecturaRead[]> {
  return apiRequest<LecturaRead[]>(`/api/v1/pagos/socios/${socioId}/pendientes`);
}

export function fetchPagosSocio(socioId: number, limit = 50): Promise<PagoRead[]> {
  return apiRequest<PagoRead[]>(`/api/v1/pagos/socios/${socioId}?limit=${limit}`);
}

export function fetchMultasSocio(socioId: number, soloPendientes = false): Promise<MultaRead[]> {
  const qs = soloPendientes ? "?solo_pendientes=true" : "";
  return apiRequest<MultaRead[]>(`/api/v1/multas/socios/${socioId}${qs}`);
}

export function fetchSubsidioVigente(contratoId: number): Promise<SubsidioRead | null> {
  return apiRequest<SubsidioRead | null>(
    `/api/v1/subsidios/contrato/${contratoId}/vigente`,
  );
}

export function registrarPago(
  payload: PagoTomarPayload,
  idempotencyKey?: string,
): Promise<PagoRead> {
  return apiRequest<PagoRead>("/api/v1/pagos", {
    method: "POST",
    body: payload,
    ...(idempotencyKey ? { idempotencyKey } : {}),
  });
}

export interface ResumenCuenta {
  saldo_total: number;
  deuda_pendiente: number;
  saldo_a_favor: number;
  boletas_pendientes: number;
  boletas_vencidas: number;
}

export function fetchResumenCuenta(socioId: number): Promise<ResumenCuenta> {
  return apiRequest<ResumenCuenta>(`/api/v1/pagos/socios/${socioId}/resumen`);
}

/** Imputa el saldo a favor a las boletas pendientes (normalmente pasa solo). */
export function aplicarSaldoFavor(socioId: number): Promise<PagoRead | null> {
  return apiRequest<PagoRead | null>(
    `/api/v1/pagos/socios/${socioId}/aplicar-saldo-favor`,
    { method: "POST" },
  );
}

export function descargarReciboPdf(pagoId: number): Promise<void> {
  return apiDownload(`/api/v1/pagos/${pagoId}/recibo.pdf`, `recibo_${pagoId}.pdf`);
}

/** PDF de una boleta (la misma que se reparte), para reimprimirla en el mesón. */
export function descargarBoletaPdf(lecturaId: number, numero: string | null): Promise<void> {
  return apiDownload(
    `/api/v1/lecturas/${lecturaId}/boleta-pdf`,
    `boleta_${numero ?? lecturaId}.pdf`,
  );
}

export function anularPago(pagoId: number, motivo: string): Promise<PagoRead> {
  return apiRequest<PagoRead>(`/api/v1/pagos/${pagoId}/anular`, {
    method: "POST",
    body: { motivo },
  });
}
